import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, ChevronLeft, ChevronRight, ExternalLink, Flag, Loader2, ShieldCheck, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDismissReport,
  usePostReports,
  useResolveReport,
  type PostReportRow,
  type ReportStatus,
} from "@/hooks/useReports";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 20;

/** /moderation — forum-post moderation queue. Three tabs (Pending /
 *  Resolved / Dismissed) over the same paginated list. Director+ only. */
export default function Moderation() {
  const [status, setStatus] = useState<ReportStatus>("pending");
  const [page,   setPage]   = useState(1);
  const list = usePostReports({ status, page });

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <header className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
          <ShieldCheck className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Moderation</h1>
          <p className="text-sm text-muted-foreground">
            Forum posts users have flagged for review. Resolve hides the post; dismiss closes the report without action.
          </p>
        </div>
      </header>

      <div className="flex gap-2">
        <TabButton current={status} value="pending"   onSelect={(v) => { setStatus(v); setPage(1); }}>Pending</TabButton>
        <TabButton current={status} value="resolved"  onSelect={(v) => { setStatus(v); setPage(1); }}>Resolved</TabButton>
        <TabButton current={status} value="dismissed" onSelect={(v) => { setStatus(v); setPage(1); }}>Dismissed</TabButton>
      </div>

      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b border-border/60 bg-muted/30 py-3">
          <CardTitle className="text-base">
            {status === "pending"   && "Waiting on you"}
            {status === "resolved"  && "Resolved (post hidden)"}
            {status === "dismissed" && "Dismissed (no action)"}
          </CardTitle>
          {list.data && <Badge variant="outline" className="font-mono tabular-nums">{list.data.count}</Badge>}
        </CardHeader>
        <CardContent className="p-0">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
            </div>
          ) : list.data && list.data.results.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {list.data.results.map((r) => <ReportRow key={r.id} report={r} />)}
            </ul>
          ) : (
            <EmptyState status={status} />
          )}
        </CardContent>
      </Card>

      {list.data && list.data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold tabular-nums">{list.data.count.toLocaleString()}</span>{" "}
            report{list.data.count === 1 ? "" : "s"}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!list.data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
              {page} / {totalPages}
            </div>
            <Button variant="outline" size="sm" disabled={!list.data.next}
              onClick={() => setPage((p) => p + 1)}>
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReportRow({ report }: { report: PostReportRow }) {
  const resolve = useResolveReport();
  const dismiss = useDismissReport();
  const confirm = useConfirm();
  const pending = report.status === "pending";

  return (
    <li className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge variant="outline" className={cn("border font-medium", reasonClass(report.reason))}>
          <Flag className="mr-1 h-3 w-3" />{report.reason_display}
        </Badge>
        <Badge variant="outline" className={cn("border font-medium", statusClass(report.status))}>
          {report.status_display}
        </Badge>
        <span className="text-muted-foreground">
          Reported by <span className="font-medium text-foreground">{report.reporter_name}</span>
          {" · "}{formatDateTime(report.created_at)}
        </span>
      </div>

      <div className="rounded-md border bg-muted/30 p-3">
        <div className="text-xs text-muted-foreground">
          Post by <span className="font-medium text-foreground">{report.post_author_name || "—"}</span>
          {" in "}
          <Link to={`/forum/threads/${report.thread_id}`}
                className="font-medium text-primary hover:underline">
            {report.thread_title}
            <ExternalLink className="ml-1 inline h-3 w-3" />
          </Link>
        </div>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">
          {report.post_excerpt || <em className="text-muted-foreground">(empty)</em>}
        </p>
      </div>

      {report.note && (
        <div className="rounded-md border-l-2 border-primary/40 bg-primary/5 px-3 py-2 text-xs">
          <span className="font-semibold">Reporter note: </span>
          {report.note}
        </div>
      )}

      {!pending && report.resolved_by_name && (
        <div className="text-xs text-muted-foreground">
          {report.status === "resolved" ? "Resolved" : "Dismissed"} by{" "}
          <span className="font-medium text-foreground">{report.resolved_by_name}</span>
          {" · "}{formatDateTime(report.resolved_at)}
        </div>
      )}

      {pending && (
        <div className="flex justify-end gap-2">
          <Button
            size="sm" variant="outline"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate(report.id)}
          >
            {dismiss.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <X className="mr-2 h-4 w-4" />}
            Dismiss
          </Button>
          <Button
            size="sm"
            disabled={resolve.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "Resolve and hide the post?",
                description: "The post will be hidden from the forum. The report stays here for the audit trail.",
                confirmLabel: "Resolve & hide",
              });
              if (!ok) return;
              resolve.mutate(report.id);
            }}
          >
            {resolve.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <Check className="mr-2 h-4 w-4" />}
            Resolve &amp; hide
          </Button>
        </div>
      )}
    </li>
  );
}

function TabButton({
  current, value, onSelect, children,
}: {
  current: ReportStatus;
  value: ReportStatus;
  onSelect: (v: ReportStatus) => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant={current === value ? "secondary" : "ghost"}
      size="sm"
      onClick={() => onSelect(value)}
    >
      {children}
    </Button>
  );
}

function EmptyState({ status }: { status: ReportStatus }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <ShieldCheck className="h-5 w-5 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-semibold">
        {status === "pending"
          ? "Nothing waiting"
          : status === "resolved"
            ? "No resolved reports yet"
            : "No dismissed reports yet"}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {status === "pending"
          ? "The forum's calm — no flagged posts to review."
          : "Resolved + dismissed reports stay here for the audit trail."}
      </p>
    </div>
  );
}

function statusClass(s: ReportStatus): string {
  return s === "pending"
    ? "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30"
    : s === "resolved"
      ? "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30"
      : "bg-muted/40 text-foreground/80 border-border";
}

function reasonClass(r: PostReportRow["reason"]): string {
  switch (r) {
    case "spam":           return "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30";
    case "harassment":     return "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30";
    case "offtopic":       return "bg-muted/40 text-foreground/80 border-border";
    case "misinformation": return "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30";
    default:               return "bg-muted/40 text-foreground/80 border-border";
  }
}
